// How a counter display's elements enter when its screen changes. The keyframes are
// in styles/surfaces.css (.counter-shell); the server checks the values (internal/layout).
import { useSyncExternalStore } from 'react'
import type { StaffKey } from '../lib/staffText'
import type { LayoutBindings } from './render'
import type { CounterScreen, ElementKind, ElementMotion, LayoutCanvas, LayoutElement, ScreenChange } from './types'

/** What an element without a motion does: the owner's "texts slide from top to bottom". */
export const DEFAULT_MOTION: ElementMotion = 'slideDown'

const NAMES: Record<ElementMotion, StaffKey> = {
  slideDown: 'designs.motion.slideDown',
  rise: 'designs.motion.rise',
  flip: 'designs.motion.flip',
  fade: 'designs.motion.fade',
  none: 'designs.motion.none',
  draw: 'designs.motion.draw',
}

const EVERY: ElementMotion[] = ['slideDown', 'rise', 'flip', 'fade', 'none']

/** The inspector's choices for a kind of element; only a rule is drawn in. */
export function motionChoices(kind: ElementKind): Array<{ value: ElementMotion; label: StaffKey }> {
  const motions = kind === 'rule' ? [...EVERY, 'draw' as const] : EVERY
  return motions.map((value) => ({ value, label: NAMES[value] }))
}

/** The element's chosen motion; absent is the default. */
export function motionOf(el: Pick<LayoutElement, 'motion'>): ElementMotion {
  return el.motion ?? DEFAULT_MOTION
}

/** What plays: with reduced motion, anything that moves only fades. */
export function motionShown(el: Pick<LayoutElement, 'motion'>, reduced: boolean): ElementMotion {
  const chosen = motionOf(el)
  return reduced && chosen !== 'none' ? 'fade' : chosen
}

/** The element with a motion chosen; the default is stored as absent, as every older design has it. */
export function withMotion(el: LayoutElement, value: ElementMotion): LayoutElement {
  const { motion: _, ...rest } = el
  return value === DEFAULT_MOTION ? rest : { ...rest, motion: value }
}

/** A counter design without a screen change flips like a flip clock. */
export const DEFAULT_SCREEN_CHANGE: ScreenChange = 'flip'

const CHANGE_NAMES: Record<ScreenChange, StaffKey> = {
  flip: 'designs.change.flip',
  slide: 'designs.change.slide',
  fade: 'designs.change.fade',
  none: 'designs.change.none',
}

export const SCREEN_CHANGES = (['flip', 'slide', 'fade', 'none'] as const).map((value) => ({ value, label: CHANGE_NAMES[value] }))

export function screenChangeOf(canvas: Pick<LayoutCanvas, 'screenChange'> | undefined): ScreenChange {
  return canvas?.screenChange ?? DEFAULT_SCREEN_CHANGE
}

/** The canvas with a screen change chosen; the default is stored as absent. */
export function withScreenChange(canvas: LayoutCanvas, value: ScreenChange): LayoutCanvas {
  const { screenChange: _, ...rest } = canvas
  return value === DEFAULT_SCREEN_CHANGE ? rest : { ...rest, screenChange: value }
}

/** The entrance an element plays: its own motion only under Slide; a flip clock flips cards instead. */
export function entranceShown(change: ScreenChange, el: Pick<LayoutElement, 'motion'>, reduced: boolean): ElementMotion {
  switch (change) {
    case 'slide':
      return motionShown(el, reduced)
    case 'fade':
      return 'fade'
    case 'none':
      return 'none'
    default:
      return reduced ? 'fade' : 'none'
  }
}

/** What a screen showed, kept so a flip can fold it away. */
export interface FlipFrame {
  screen: CounterScreen
  bindings: LayoutBindings
}

/** A flip-clock screen change in progress; a new serial restarts every card. */
export interface ScreenFlip {
  serial: number
  from: FlipFrame
  /** A recall: only the number turns over. */
  numberOnly: boolean
}

/** Timings shared with surfaces.css (.cf-*): a card's stagger, a character's, the flap. */
export const FLIP_STEP_MS = 50
export const FLIP_STEPS = 12
export const FLIP_CHAR_MS = 60
export const FLAP_MS = 520
/** Long enough for the last card of a busy screen and an eight-character number. */
export const FLIP_MS = FLIP_STEPS * FLIP_STEP_MS + 8 * FLIP_CHAR_MS + FLAP_MS + 120

/** Each element's place in the entrance: top to bottom, then left to right. */
export function enterOrder(elements: readonly LayoutElement[]): Map<string, number> {
  const sorted = [...elements].sort((a, b) => a.y - b.y || a.x - b.x)
  return new Map(sorted.map((el, i) => [el.id, i]))
}

const QUERY = '(prefers-reduced-motion: reduce)'

function subscribe(changed: () => void): () => void {
  const list = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(QUERY) : null
  list?.addEventListener('change', changed)
  return () => list?.removeEventListener('change', changed)
}

function reducedNow(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(QUERY).matches
}

/** Whether the screen asked for less motion; follows a change of the setting. */
export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, reducedNow, () => false)
}
